import tokens from '@kluein/design-tokens';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQueryParams, NumberParam } from 'use-query-params';

import IMG_LOGO from 'assets/klue-black-600x600.png?inline';
import store from 'store';

import AuthContext from './authContext';
import useAuth from './useAuth';

export type ProviderAuthProps = {
  children: React.ReactElement;
};

const QueryParamConfig = {
  previewing: NumberParam,
};

function usePreviewingVisibilityGroup() {
  const visibilityGroupId = useSelector(store.select.auth.visibilityGroupId);
  const [{ previewing }, setQuery] = useQueryParams(QueryParamConfig);
  useEffect(() => {
    if (visibilityGroupId !== null) {
      setQuery({ previewing: visibilityGroupId }, 'replaceIn');
    }
  }, [previewing, setQuery, visibilityGroupId]);
}

function ProvideAuth(props: ProviderAuthProps) {
  const auth = useAuth();

  const logoSize = '120px';
  const { t } = useTranslation(['Common']);

  useEffect(() => {
    auth.fetchUser().catch((error: unknown) => {
      // Auth state is already cleared inside fetchUser.
      // Log so API errors (e.g. 500s) aren't silently lost.
      // eslint-disable-next-line no-console
      console.error('fetchUser failed:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePreviewingVisibilityGroup();

  return (
    <AuthContext.Provider key="auth-provider" value={auth}>
      <AnimatePresence>
        {auth.loading && (
          <motion.div
            key="overlay"
            style={{
              backgroundColor: tokens.color.neutral.rosegrey.main,
              height: '100vh',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              zIndex: 9999,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{
              opacity: 0,
              transition: { duration: 0.5, ease: 'easeOut' },
            }}
          >
            <motion.div
              key="logo"
              initial={{ opacity: 1 }}
              animate={{
                opacity: [1, 0.88, 1],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                repeatType: 'loop',
                ease: 'easeInOut',
              }}
              exit={{
                opacity: 0,
                scale: 0.95,
                transition: { duration: 0.25, ease: 'easeOut' },
              }}
            >
              <img
                width={logoSize}
                height={logoSize}
                src={IMG_LOGO}
                alt={t('Common:logo', { company: 'Klue' })}
                style={{
                  borderRadius: tokens.borderRadius.small,
                  boxShadow: 'rgba(0, 0, 0, 0.2) 0px 20px 20px -5px',
                }}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {!auth.loading ? props.children : null}
    </AuthContext.Provider>
  );
}

export default ProvideAuth;
